import { useEffect, useState } from "react";
import { ListChecks, Loader2, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { api, errMsg } from "../lib/api";

const DEFAULTS = [{ key: "service", label: "Service" }, { key: "quality", label: "Quality" }, { key: "cleanliness", label: "Cleanliness" }];
const MAX = 6;

export const SurveyEditorDialog = ({ loc, multi, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState(loc.categories || DEFAULTS);
  const [applyAll, setApplyAll] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setCats(loc.categories || DEFAULTS); setApplyAll(false); } }, [open, loc]);

  const update = (i, label) => setCats((c) => c.map((x, j) => (j === i ? { ...x, label } : x)));
  const remove = (i) => setCats((c) => c.filter((_, j) => j !== i));
  const add = () => setCats((c) => [...c, { key: null, label: "" }]);

  const save = async () => {
    const labels = cats.map((c) => c.label.trim().toLowerCase());
    if (labels.some((l) => !l)) return toast.error("Please name every category or remove the empty one");
    if (new Set(labels).size !== labels.length) return toast.error("Category names must be unique");
    setSaving(true);
    try {
      await api.put(`/business/locations/${loc.id}/survey`, { categories: cats.map((c) => ({ key: c.key, label: c.label.trim() })), apply_all: applyAll });
      toast.success(applyAll ? "Survey updated for all locations" : "Survey updated");
      setOpen(false);
      await onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const current = loc.categories || DEFAULTS;
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid="survey-editor-panel">
      <div className="flex items-start gap-3 min-w-0">
        <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-[#F5ECD9] text-[#9A6B1F]"><ListChecks className="w-4 h-4" /></span>
        <div className="min-w-0">
          <p className="font-semibold text-[#1A2B25]">Survey categories</p>
          <p className="text-sm text-[#4B5A54]" data-testid="survey-editor-summary">{current.length ? current.map((c) => c.label).join(", ") : "Overall rating only"}</p>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button className="btn-secondary whitespace-nowrap" data-testid="survey-editor-button">Customize survey</button>
        </DialogTrigger>
        <DialogContent className="rounded-3xl sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="survey-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Customize survey</DialogTitle>
            <DialogDescription>Choose the categories customers rate with stars at {loc.label}. The overall 1–5 star rating is always included.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-[#4B5A54]">Overall satisfaction <span className="font-normal">(always on)</span></div>
            {cats.map((c, i) => (
              <div key={c.key || `new-${i}`} className="flex items-center gap-2">
                <input value={c.label} maxLength={40} onChange={(e) => update(i, e.target.value)} placeholder="e.g. Atmosphere"
                  className="input-base" data-testid={`survey-category-input-${i}`} />
                <button onClick={() => remove(i)} className="p-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors" aria-label="Remove category" data-testid={`survey-category-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2 pt-1">
              <button onClick={add} disabled={cats.length >= MAX} className="btn-secondary disabled:opacity-50" data-testid="survey-category-add">
                <Plus className="w-4 h-4" />Add category
              </button>
              <button onClick={() => setCats(DEFAULTS)} className="btn-secondary" data-testid="survey-category-reset"><RotateCcw className="w-4 h-4" />Reset to default</button>
            </div>
            <p className="text-xs text-[#778580]">Up to {MAX} categories. Renaming keeps past scores for that category; removing hides it.</p>
            {multi && (
              <label htmlFor="survey-apply-all-checkbox" className="flex items-center gap-3 pt-2 text-sm cursor-pointer">
                <Checkbox id="survey-apply-all-checkbox" checked={applyAll} onCheckedChange={(v) => setApplyAll(!!v)} data-testid="survey-apply-all-checkbox" />
                Apply to all my locations
              </label>
            )}
          </div>
          <button onClick={save} disabled={saving} className="btn-primary w-full justify-center !py-3" data-testid="survey-editor-save">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save survey"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
